import React, { useState } from 'react';

export default function ApartmentSearchForm({
  filters,
  regionData,
  districtData,
  subdistrictData,
  onFilterChange,
  onSearch,
  onReset
}) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // 가격 포맷팅 함수
  const formatPriceDisplay = (price) => {
    if (!price) return '';
    const numPrice = parseInt(price);
    if (numPrice >= 10000) {
      const uk = Math.floor(numPrice / 10000);
      const chun = numPrice % 10000;
      return chun > 0 ? `${uk}억 ${chun.toLocaleString()}만원` : `${uk}억원`;
    }
    return `${numPrice.toLocaleString()}만원`;
  };
  
  // 세대수 옵션
  const householdOptions = [
    { label: '전체', value: '' },
    { label: '500세대 미만', value: '0-499' },
    { label: '500-1000세대', value: '500-999' },
    { label: '1000세대 이상 (대단지)', value: '1000-' }
  ];
  
  // 면적 옵션 (min 이상, max 미만)
  const areaOptions = [
    { label: '전체', value: '' },
    { label: '59㎡ 미만', value: '0-59' },
    { label: '59-60㎡', value: '59-60' },
    { label: '60-84㎡', value: '60-84' },
    { label: '84-85㎡', value: '84-85' },
    { label: '85-101㎡', value: '85-101' },
    { label: '101-125㎡', value: '101-125' },
    { label: '125㎡ 이상', value: '125-' }
  ];
  
  
  // 역세권 옵션
  const subwayOptions = [
    { label: '전체', value: '' },
    { label: '도보 5분 이내', value: '5' },
    { label: '도보 10분 이내', value: '10' },
    { label: '도보 15분 이내', value: '15' },
    { label: '도보 20분 이내', value: '20' },
    { label: '도보 30분 이내', value: '30' }
  ];
  
  // 가격 변동 옵션
  const priceChangeOptions = [
    { label: '전체', value: '' },
    { label: '최고가 대비 하락률 높은 순 (투자 기회)', value: 'falling' },
    { label: '최근 최고가 갱신 (상승세)', value: 'peak' },
    { label: '전세가율 높은 순 (갭투자)', value: 'jeonse_ratio' }
  ];
  
  // 정렬 옵션
  const sortOptions = [
    { label: '매매가', value: 'sale_price' },
    { label: '변동률', value: 'peak_comparison' },
    { label: '세대수', value: 'total_households' },
    { label: '전세가율', value: 'jeonse_ratio' }
  ];
  
  // 결과 개수 옵션
  const limitOptions = [
    { label: 'Top 5', value: 5 },
    { label: 'Top 10', value: 10 },
    { label: 'Top 20', value: 20 },
    { label: 'Top 30', value: 30 }
  ];
  
  // 시군구 다중 선택 핸들러
  const handleDistrictChange = (districtNumber) => {
    const currentDistricts = [...filters.districts];
    const index = currentDistricts.indexOf(districtNumber);
    
    if (index > -1) {
      currentDistricts.splice(index, 1);
    } else if (currentDistricts.length < 3) {
      currentDistricts.push(districtNumber);
    } else {
      alert('시/군/구는 최대 3개까지 선택 가능합니다.');
      return;
    }
    
    onFilterChange('districts', currentDistricts);
  };
  
  // 읍면동 다중 선택 핸들러
  const handleSubdistrictChange = (subdistrictNumber) => {
    const currentSubdistricts = [...(filters.subdistricts || [])];
    const index = currentSubdistricts.indexOf(subdistrictNumber);
    
    if (index > -1) {
      currentSubdistricts.splice(index, 1);
    } else {
      currentSubdistricts.push(subdistrictNumber);
    }
    
    onFilterChange('subdistricts', currentSubdistricts);
  };
  
  // 범위 값 파싱
  const parseRangeValue = (value, type) => {
    if (!value) return { min: null, max: null };
    
    const [min, max] = value.split('-');
    return {
      min: min ? parseInt(min) : null,
      max: max ? parseInt(max) : null
    };
  };
  
  return (
    <div className="bg-[#FFFEF6] border border-gray-200 rounded-lg shadow-md p-6 mb-6 hover:shadow-lg transition-shadow">
      <h2 className="text-xl font-semibold mb-4">필터 설정</h2>
      
      {/* 지역 선택 안내 */}
      {filters.region && filters.districts.length === 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            ⚠️ 시/군/구를 최소 1개 이상 선택해야 검색이 가능합니다.
          </p>
        </div>
      )}
      
      {/* 지역 선택 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-2">시/도</label>
          <select
            className="w-full p-2 border border-gray-300 rounded"
            value={filters.region}
            onChange={(e) => onFilterChange('region', e.target.value)}
          >
            <option value="">선택하세요</option>
            {regionData.map(region => (
              <option key={region.region_number} value={region.region_number}>
                {region.region_name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            시/군/구 (최대 3개)
          </label>
          <div className="border border-gray-300 rounded p-2 max-h-32 overflow-y-auto">
            {districtData.length > 0 ? (
              districtData.map(district => (
                <label key={district.district_number} className="block">
                  <input
                    type="checkbox"
                    checked={filters.districts.includes(district.district_number)}
                    onChange={() => handleDistrictChange(district.district_number)}
                    className="mr-2"
                  />
                  {district.district_name}
                </label>
              ))
            ) : (
              <span className="text-gray-500">시/도를 먼저 선택하세요</span>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            읍/면/동 (다중 선택 가능)
          </label>
          <div className="border border-gray-300 rounded p-2 max-h-32 overflow-y-auto">
            {filters.districts.length === 0 ? (
              <span className="text-gray-500">시/군/구를 먼저 선택하세요</span>
            ) : subdistrictData.length === 0 ? (
              <span className="text-gray-500">읍/면/동 데이터가 없습니다</span>
            ) : (
              <>
                <label className="block mb-1">
                  <input
                    type="checkbox"
                    checked={(filters.subdistricts || []).length === 0}
                    onChange={() => onFilterChange('subdistricts', [])}
                    className="mr-2"
                  />
                  <span className="font-semibold">전체 선택</span>
                </label>
                <hr className="my-1" />
                {subdistrictData.map(subdistrict => (
                  <label key={subdistrict.subdistrict_number} className="block">
                    <input
                      type="checkbox"
                      checked={(filters.subdistricts || []).includes(subdistrict.subdistrict_number)}
                      onChange={() => handleSubdistrictChange(subdistrict.subdistrict_number)}
                      className="mr-2"
                    />
                    {subdistrict.subdistrict_name}
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* 기본 필터 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-2">세대수</label>
          <select
            className="w-full p-2 border border-gray-300 rounded"
            onChange={(e) => onFilterChange('households', parseRangeValue(e.target.value))}
          >
            {householdOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">면적 (㎡)</label>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <input
                type="number"
                placeholder="최소 (예: 59)"
                className="w-full p-2 border border-gray-300 rounded"
                value={filters.area?.min || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  onFilterChange('area', { ...filters.area, min: value });
                }}
              />
              {filters.area?.min && (
                <div className="text-xs text-blue-600 mt-1">
                  {filters.area.min}㎡
                </div>
              )}
            </div>
            <span className="text-gray-500">~</span>
            <div className="flex-1">
              <input
                type="number"
                placeholder="최대 (예: 84)"
                className="w-full p-2 border border-gray-300 rounded"
                value={filters.area?.max || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  onFilterChange('area', { ...filters.area, max: value });
                }}
              />
              {filters.area?.max && (
                <div className="text-xs text-blue-600 mt-1">
                  {filters.area.max}㎡
                </div>
              )}
            </div>
          </div>
          <div className="mt-1 text-xs text-gray-500">
            예시: 59㎡, 84㎡, 101㎡, 125㎡
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">가격대 (만원)</label>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <input
                type="number"
                placeholder="최소 (예: 50000)"
                className="w-full p-2 border border-gray-300 rounded"
                value={filters.price?.min || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  onFilterChange('price', { ...filters.price, min: value });
                }}
              />
              {filters.price?.min && (
                <div className="text-xs text-blue-600 mt-1">
                  {formatPriceDisplay(filters.price.min)}
                </div>
              )}
            </div>
            <span className="text-gray-500">~</span>
            <div className="flex-1">
              <input
                type="number"
                placeholder="최대 (예: 150000)"
                className="w-full p-2 border border-gray-300 rounded"
                value={filters.price?.max || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  onFilterChange('price', { ...filters.price, max: value });
                }}
              />
              {filters.price?.max && (
                <div className="text-xs text-blue-600 mt-1">
                  {formatPriceDisplay(filters.price.max)}
                </div>
              )}
            </div>
          </div>
          <div className="mt-1 text-xs text-gray-500">
            예시: 5억 = 50000, 10억 = 100000, 15억 = 150000
          </div>
        </div>
      </div>
      
      {/* 고급 필터 토글 */}
      <button
        className="text-blue-600 mb-4"
        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
      >
        {showAdvancedFilters ? '▼' : '▶'} 고급 필터
      </button>
      
      {/* 고급 필터 */}
      {showAdvancedFilters && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded">
          <div>
            <label className="block text-sm font-medium mb-2">역세권</label>
            <select
              className="w-full p-2 border border-gray-300 rounded"
              value={filters.subwayTime || ''}
              onChange={(e) => onFilterChange('subwayTime', e.target.value ? parseInt(e.target.value) : null)}
            >
              {subwayOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">가격 변동</label>
            <select
              className="w-full p-2 border border-gray-300 rounded"
              value={filters.priceChange}
              onChange={(e) => onFilterChange('priceChange', e.target.value)}
            >
              {priceChangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      {/* 정렬 및 결과 개수 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-2">정렬 기준</label>
          <select
            className="w-full p-2 border border-gray-300 rounded"
            value={filters.sortBy}
            onChange={(e) => onFilterChange('sortBy', e.target.value)}
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">정렬 순서</label>
          <select
            className="w-full p-2 border border-gray-300 rounded"
            value={filters.sortOrder}
            onChange={(e) => onFilterChange('sortOrder', e.target.value)}
          >
            <option value="desc">내림차순</option>
            <option value="asc">오름차순</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">결과 개수</label>
          <select
            className="w-full p-2 border border-gray-300 rounded"
            value={filters.limit}
            onChange={(e) => onFilterChange('limit', parseInt(e.target.value))}
          >
            {limitOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* 선택된 필터 태그 */}
      {(filters.districts.length > 0 || (filters.subdistricts && filters.subdistricts.length > 0)) && (
        <div className="mb-4">
          {filters.districts.length > 0 && (
            <div className="mb-2">
              <span className="text-sm font-medium mr-2">선택된 시/군/구:</span>
              {filters.districts.map(districtNumber => {
                const district = districtData.find(d => d.district_number === districtNumber);
                return district ? (
                  <span
                    key={districtNumber}
                    className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2"
                  >
                    {district.district_name}
                    <button
                      className="ml-1 text-blue-600"
                      onClick={() => handleDistrictChange(districtNumber)}
                    >
                      ×
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          )}
          {filters.subdistricts && filters.subdistricts.length > 0 && (
            <div>
              <span className="text-sm font-medium mr-2">선택된 읍/면/동:</span>
              {filters.subdistricts.map(subdistrictNumber => {
                const subdistrict = subdistrictData.find(s => s.subdistrict_number === subdistrictNumber);
                return subdistrict ? (
                  <span
                    key={subdistrictNumber}
                    className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-sm mr-2"
                  >
                    {subdistrict.subdistrict_name}
                    <button
                      className="ml-1 text-green-600"
                      onClick={() => handleSubdistrictChange(subdistrictNumber)}
                    >
                      ×
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      )}
      
      {/* 버튼 */}
      <div className="flex gap-4">
        <button
          className={`px-4 py-1.5 rounded transition-all duration-200 ${
            filters.districts.length > 0 
              ? 'bg-green-600 text-white hover:bg-green-700 hover:scale-105 cursor-pointer' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          onClick={onSearch}
          disabled={filters.districts.length === 0}
          title={filters.districts.length === 0 ? '시/군/구를 최소 1개 선택해주세요' : ''}
        >
          검색
        </button>
        <button
          className="bg-gray-500 text-white px-4 py-1.5 rounded hover:bg-gray-600 transition-colors duration-200"
          onClick={onReset}
        >
          초기화
        </button>
      </div>
    </div>
  );
}